import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, AlertTriangle } from 'lucide-react';
import { useSales } from '../hooks/useSales';
import { useCatalog } from '../hooks/useCatalog';
import { blankLine, computeDocument, lineFromProduct, money, stockWarnings } from '../utils/quotes';
import { addDaysStr } from '../utils/customers';
import { CustomerPicker, LinesTable, ProductPicker, inputCls, labelCls } from '../components/DocumentLines';

const btn = 'text-xs font-bold px-3 py-2 rounded-xl flex items-center gap-1.5 disabled:opacity-50';
const btnGhost = `${btn} border border-seryi-bg-light text-seryi-heading`;
const btnMain = `${btn} bg-seryi-green text-seryi-bg-dark font-extrabold`;

// Venta a mano (mostrador): sin cotización. Las líneas, el total y las existencias las calcula y valida la base al crearla.
export default function NewSalePage() {
  const navigate = useNavigate();
  const { customers, settings, loading: loadingSales, tableMissing, createManualSale, isReadOnlyDemo } = useSales({ withList: false });
  const { products, lists, priceMap, defaultKey, loading: loadingCatalog } = useCatalog();
  const [customerId, setCustomerId] = useState(null);
  const [listKey, setListKey] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [lines, setLines] = useState([]);
  const [busy, setBusy] = useState(false);

  const customer = customers.find((c) => c.id === customerId) || null;
  const activeList = listKey || customer?.price_list_key || defaultKey;
  const productsById = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);
  const doc = useMemo(() => computeDocument(lines), [lines]);
  const warnings = useMemo(() => stockWarnings(lines, productsById), [lines, productsById]);
  const blocked = settings.stock_policy === 'block' && warnings.length > 0;

  const setLine = (i, patch) => setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addProduct = (p) => setLines((prev) => {
    const same = prev.findIndex((l) => l.product_id === p.id);
    if (same >= 0) return prev.map((l, i) => (i === same ? { ...l, quantity: Number(l.quantity || 0) + 1 } : l));
    return [...prev, lineFromProduct(p, { priceMap, listKey: activeList, defaultKey, position: prev.length })];
  });

  const create = async () => {
    setBusy(true);
    const row = await createManualSale({ customer_id: customerId, lines, due_date: dueDate || null });
    setBusy(false);
    if (row?.sale_id) navigate(`/v2/ventas/${row.sale_id}`, { state: { warnings: row.warnings || [] } });
  };

  if (tableMissing) return <div className="p-6 text-xs text-amber-300">Falta ejecutar la migración <code>20261003c_sales.sql</code> en Supabase para usar las ventas.</div>;
  if (loadingSales || loadingCatalog) return <p className="text-xs text-seryi-text">Cargando…</p>;

  return (
    <div className="space-y-5 max-w-6xl">
      <Link to="/v2/ventas" className="text-[11px] text-seryi-text hover:text-seryi-heading flex items-center gap-1"><ArrowLeft size={12} /> Ventas</Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-extrabold text-seryi-heading">Nueva venta</h2>
          <p className="text-[11px] text-seryi-text mt-1">Venta directa, sin cotización. Al crearla se descuentan las existencias de los productos del catálogo.</p>
        </div>
        <button type="button" disabled={busy || isReadOnlyDemo || !doc.ok || lines.length === 0 || blocked} onClick={create} className={btnMain}>{busy ? 'Creando…' : 'Crear venta'}</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <CustomerPicker customers={customers} value={customerId} onPick={(c) => { setCustomerId(c?.id || null); if (c?.price_list_key) setListKey(c.price_list_key); }} />
        <div className="space-y-1">
          <label htmlFor="s-list" className={labelCls}>Lista de precios</label>
          <select id="s-list" className={inputCls} disabled={lists.length === 0} value={activeList || ''} onChange={(e) => setListKey(e.target.value)}>
            {lists.map((l) => <option key={l.key} value={l.key}>{l.name}{l.is_default ? ' (predeterminada)' : ''}</option>)}
            {lists.length === 0 && <option value="">Sin listas</option>}
          </select>
          <p className="text-[10px] text-seryi-text">Solo cambia el precio de los productos que agregues después.</p>
        </div>
        <div className="space-y-1">
          <label htmlFor="s-due" className={labelCls}>Vence (opcional)</label>
          <input id="s-due" type="date" className={inputCls} value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          <div className="flex gap-2 text-[10px]">
            {[15, 30, 60].map((d) => <button key={d} type="button" onClick={() => setDueDate(addDaysStr(d))} className="text-seryi-green font-bold hover:underline">+{d} días</button>)}
          </div>
        </div>
      </div>

      <ProductPicker products={products} priceMap={priceMap} listKey={activeList} defaultKey={defaultKey} onAdd={addProduct} />
      <LinesTable editable lines={lines} errors={doc.errors} lineTotals={doc.lineTotals} onChange={setLine} onRemove={(i) => setLines((prev) => prev.filter((_, idx) => idx !== i))} />
      <button type="button" onClick={() => setLines((prev) => [...prev, blankLine(prev.length)])} className={btnGhost}><Plus size={13} /> Línea libre (sin catálogo)</button>

      {warnings.length > 0 && (
        <div className={`p-3 rounded-xl border text-xs space-y-0.5 ${blocked ? 'border-red-500/40 bg-red-500/10 text-red-200' : 'border-amber-500/40 bg-amber-500/10 text-amber-200'}`}>
          {warnings.map((w) => <p key={w.product_id}><AlertTriangle size={12} className="inline mr-1" />{w.name}: pides {w.needed} y hay {w.stock} en existencias.{blocked ? ' Tu política no deja vender más de lo que hay.' : ''}</p>)}
        </div>
      )}

      <div className="flex justify-end">
        <div className="bg-seryi-bg border border-seryi-bg-light rounded-2xl p-4 text-xs text-seryi-heading space-y-1.5 w-full md:w-80">
          <div className="flex justify-between"><span className="text-seryi-text">Subtotal</span><span>{money(doc.totals.subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-seryi-text">Descuentos</span><span>−{money(doc.totals.discount_total)}</span></div>
          <div className="flex justify-between"><span className="text-seryi-text">Impuestos</span><span>{money(doc.totals.tax_total)}</span></div>
          <div className="flex justify-between text-base font-black border-t border-seryi-bg-light pt-2"><span>Total ({settings.currency})</span><span className="text-emerald-400">{money(doc.totals.total)}</span></div>
        </div>
      </div>
    </div>
  );
}
